import { useRef } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Camera, Upload, CheckCircle, FileText, AlertCircle } from "lucide-react";

function FileUploadBox({ label, file, inputRef, onChange, captureMode, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0A2540] text-xs font-medium">{label}</Label>
      <div
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all hover:border-[#0A2540]/50 ${file ? "border-[#10B981] bg-[#10B981]/5" : "border-[#CBD5E1]"}`}
        data-testid={testId}
      >
        <input ref={inputRef} type="file" accept="image/*" capture={captureMode ? "environment" : undefined} onChange={onChange} className="hidden" />
        {file ? (
          <div className="flex items-center gap-2 justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
            <span className="text-xs text-[#10B981] font-medium truncate max-w-[180px]">{file.name}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#64748B]" />
              <Upload className="w-4 h-4 text-[#64748B]" />
            </div>
            <span className="text-xs text-[#64748B]">Prendre une photo ou choisir un fichier</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReferrerNameField({ value, onChange, testId = "register-referrer-name" }) {
  const { UserPlus } = require("lucide-react");
  return (
    <div className="space-y-1.5 pt-2 border-t border-[#E2E8F0]">
      <Label className="text-[#0A2540] font-medium flex items-center gap-1.5">
        <UserPlus className="w-4 h-4" /> Nom du référent <span className="text-[10px] font-normal text-[#94A3B8] ml-1">(optionnel)</span>
      </Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ex: Jean Mbeng (qui vous a parlé de TrigoTour)"
        className="h-11 border-[#CBD5E1]"
        data-testid={testId}
      />
      <p className="text-[10px] text-[#94A3B8]">Si quelqu'un vous a recommandé TrigoTour, indiquez son nom.</p>
    </div>
  );
}

export function DriverDocumentsStep({ cniNumber, setCniNumber, licenseNumber, setLicenseNumber, cniFront, setCniFront, cniBack, setCniBack, licenseFront, setLicenseFront, licenseBack, setLicenseBack, profilePhoto, profilePreview, onProfilePhoto, setError, referrerName, setReferrerName }) {
  const cniFrontRef = useRef(null);
  const cniBackRef = useRef(null);
  const licenseFrontRef = useRef(null);
  const licenseBackRef = useRef(null);
  const profilePhotoRef = useRef(null);

  const handleFileSelect = (setter) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { setError("Fichier trop volumineux (max 10MB)"); return; }
      setter(file);
      setError("");
    }
  };

  return (
    <>
      {/* Profile Photo */}
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium flex items-center gap-1.5">
          <Camera className="w-4 h-4" /> Photo de profil (obligatoire)
        </Label>
        <div
          onClick={() => profilePhotoRef.current?.click()}
          className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all hover:border-[#0A2540]/50 ${profilePhoto ? "border-[#10B981] bg-[#10B981]/5" : "border-[#CBD5E1]"}`}
          data-testid="upload-profile-photo"
        >
          <input ref={profilePhotoRef} type="file" accept="image/*" capture="user" onChange={onProfilePhoto} className="hidden" />
          {profilePreview ? (
            <div className="flex items-center gap-3 justify-center">
              <img src={profilePreview} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-[#10B981]" />
              <div className="text-left">
                <p className="text-xs text-[#10B981] font-medium">Photo selectionnee</p>
                <p className="text-xs text-[#64748B] truncate max-w-[150px]">{profilePhoto?.name}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <div className="w-14 h-14 rounded-full bg-[#F8FAFC] border border-[#CBD5E1] flex items-center justify-center mb-1">
                <Camera className="w-6 h-6 text-[#64748B]" />
              </div>
              <span className="text-xs text-[#64748B]">Prendre un selfie ou choisir une photo</span>
            </div>
          )}
        </div>
      </div>

      {/* CNI */}
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium flex items-center gap-1.5">
          <FileText className="w-4 h-4" /> Carte Nationale d'Identite (CNI)
        </Label>
        <Input value={cniNumber} onChange={(e) => setCniNumber(e.target.value)} placeholder="Numero de CNI" className="h-11 border-[#CBD5E1]" required data-testid="register-cni-number" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FileUploadBox label="CNI Recto" file={cniFront} inputRef={cniFrontRef} onChange={handleFileSelect(setCniFront)} captureMode testId="upload-cni-front" />
        <FileUploadBox label="CNI Verso" file={cniBack} inputRef={cniBackRef} onChange={handleFileSelect(setCniBack)} captureMode testId="upload-cni-back" />
      </div>

      {/* License */}
      <div className="space-y-1.5 mt-2">
        <Label className="text-[#0A2540] font-medium flex items-center gap-1.5">
          <FileText className="w-4 h-4" /> Permis de Conduire
        </Label>
        <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="Numero de permis" className="h-11 border-[#CBD5E1]" required data-testid="register-license-number" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FileUploadBox label="Permis Recto" file={licenseFront} inputRef={licenseFrontRef} onChange={handleFileSelect(setLicenseFront)} captureMode testId="upload-license-front" />
        <FileUploadBox label="Permis Verso" file={licenseBack} inputRef={licenseBackRef} onChange={handleFileSelect(setLicenseBack)} captureMode testId="upload-license-back" />
      </div>

      <div className="bg-[#0A2540]/5 border border-[#CBD5E1] rounded-lg p-3">
        <p className="text-xs text-[#64748B]">Vos documents seront examines par notre equipe. Vous recevrez l'approbation sous 24-48h.</p>
      </div>

      <ReferrerNameField value={referrerName || ""} onChange={setReferrerName} testId="register-referrer-name-driver" />
    </>
  );
}

export function BasicInfoStep({ name, setName, email, setEmail, phone, setPhone, password, setPassword, role, setRole, showPw, setShowPw, referrerName, setReferrerName }) {
  const { Eye, EyeOff, Users, Truck } = require("lucide-react");
  return (
    <>
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium">Vous etes</Label>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setRole("passenger")}
            className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${role === "passenger" ? "border-[#0A2540] bg-[#0A2540]/5" : "border-[#CBD5E1] hover:border-[#0A2540]/30"}`}
            data-testid="role-passenger">
            <Users className="w-5 h-5 text-[#0A2540]" /><span className="text-sm font-medium text-[#0A2540]">Passager</span>
          </button>
          <button type="button" onClick={() => setRole("driver")}
            className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${role === "driver" ? "border-[#0A2540] bg-[#0A2540]/5" : "border-[#CBD5E1] hover:border-[#0A2540]/30"}`}
            data-testid="role-driver">
            <Truck className="w-5 h-5 text-[#0A2540]" /><span className="text-sm font-medium text-[#0A2540]">Chauffeur</span>
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium">Nom complet</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jean Dupont" className="h-11 border-[#CBD5E1]" required data-testid="register-name" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium">Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" className="h-11 border-[#CBD5E1]" required data-testid="register-email" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium">Telephone</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+237 6XX XXX XXX" className="h-11 border-[#CBD5E1]" data-testid="register-phone" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[#0A2540] font-medium">Mot de passe</Label>
        <div className="relative">
          <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 caracteres" className="h-11 border-[#CBD5E1] pr-10" required data-testid="register-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {role === "driver" && (
        <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-[#EAB308] mt-0.5 shrink-0" />
          <p className="text-xs text-[#0A2540]">En tant que chauffeur, vous devrez fournir vos documents (CNI et permis de conduire) a l'etape suivante.</p>
        </div>
      )}
      {/* Show referrer field only for passenger here; drivers see it at step 2 (last field before submit) */}
      {role === "passenger" && (
        <ReferrerNameField value={referrerName || ""} onChange={setReferrerName} testId="register-referrer-name-passenger" />
      )}
    </>
  );
}
